// daemon 连接状态四态机：live / reconnecting / offline / daemon_down。
// - live：EventSource 已 open，且 30s 内收到 heartbeat 或业务事件。
// - reconnecting：EventSource 曾经 open 过但当前断开，且 /healthz 仍 ok。
// - offline：从未 open 过（鉴权失败或 SSE 不通），或 30s 无心跳。
// - daemon_down：连续 3 次 /healthz 失败。
//
// 设计要点：onerror 在"从未 open 过"时只切 offline，不抢成 reconnecting，
// 避免未登录访问者看到一直跳动的 🟡 风暴（401 触发 onerror 又自动重连
// 又再 401，无限循环）。

import { useEffect, useRef, useState, useCallback } from "react";
import { retainSSEChannel, type SSEChannel } from "./sseChannel";

export type ConnectionState = "live" | "reconnecting" | "offline" | "daemon_down";

interface ConnectionStatus {
  state: ConnectionState;
  lastEventAt: number | null;
  retry: () => void;
}

const HEARTBEAT_TIMEOUT_MS = 30_000;
const HEALTH_PING_MS = 5_000;
const HEALTH_FAIL_THRESHOLD = 3;

export function useConnectionStatus(): ConnectionStatus {
  const [state, setState] = useState<ConnectionState>("offline");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const channelRef = useRef<SSEChannel | null>(null);
  const healthFailRef = useRef(0);
  const lastEventRef = useRef<number | null>(null);
  const hasEverOpenedRef = useRef(false);
  const tickRef = useRef(0);
  const closeStreamRef = useRef<(() => void) | null>(null);

  const openStream = useCallback(() => {
    closeStreamRef.current?.();
    closeStreamRef.current = null;
    const channel = retainSSEChannel("/events");
    if (!channel) return;
    const markLive = () => {
      const now = Date.now();
      lastEventRef.current = now;
      setLastEventAt(now);
      setState((prev) => (prev === "daemon_down" ? prev : "live"));
    };
    const handleOpen = () => {
      hasEverOpenedRef.current = true;
      markLive();
    };
    const handleError = () => {
      // 从未 open 过 = 鉴权失败或 daemon 不通；切 offline 不进 reconnecting 风暴。
      // EventSource 在 401 后会自动重连，必须主动 close 否则浏览器每几秒就再 401 一次。
      if (!hasEverOpenedRef.current) {
        channel.close();
        setState((prev) => (prev === "daemon_down" ? prev : "offline"));
        return;
      }
      setState((prev) => (prev === "daemon_down" ? prev : "reconnecting"));
    };
    channel.addEventListener("open", handleOpen as EventListener);
    channel.addEventListener("message", markLive as EventListener);
    channel.addEventListener("heartbeat", markLive as EventListener);
    channel.addEventListener("error", handleError as EventListener);
    channelRef.current = channel;
    closeStreamRef.current = () => {
      channel.removeEventListener("open", handleOpen as EventListener);
      channel.removeEventListener("message", markLive as EventListener);
      channel.removeEventListener("heartbeat", markLive as EventListener);
      channel.removeEventListener("error", handleError as EventListener);
      channel.release();
    };
  }, []);

  const retry = useCallback(() => {
    healthFailRef.current = 0;
    hasEverOpenedRef.current = false;
    setState("offline");
    openStream();
  }, [openStream]);

  useEffect(() => {
    openStream();
    return () => {
      closeStreamRef.current?.();
      closeStreamRef.current = null;
      channelRef.current = null;
    };
  }, [openStream]);

  // SSE 心跳新鲜时不打 /healthz；只有心跳超时、尚未连上或正在重连时才探测。
  useEffect(() => {
    const id = window.setInterval(async () => {
      tickRef.current += 1;
      const last = lastEventRef.current;
      if (last && Date.now() - last <= HEARTBEAT_TIMEOUT_MS) {
        healthFailRef.current = 0;
        return;
      }
      try {
        const res = await fetch("/healthz", { credentials: "include" });
        if (res.ok) {
          healthFailRef.current = 0;
          // 心跳超时但 healthz 仍 ok → offline（SSE 卡住）
          if (last && Date.now() - last > HEARTBEAT_TIMEOUT_MS) {
            setState((prev) => (prev === "daemon_down" ? prev : "offline"));
          }
        } else {
          throw new Error("healthz not ok");
        }
      } catch {
        healthFailRef.current += 1;
        if (healthFailRef.current >= HEALTH_FAIL_THRESHOLD) {
          setState("daemon_down");
        }
      }
    }, HEALTH_PING_MS);
    return () => window.clearInterval(id);
  }, []);

  return { state, lastEventAt, retry };
}
